import React, { useState } from 'react';
import { Card, Tab, TabGroup, TabList, TabPanel, TabPanels, Table, TableHead, TableRow, TableHeaderCell, TableBody, TableCell, Badge, Text, TextInput, Select, SelectItem } from '@tremor/react';
import { Vehicle, ParkingType } from '../../types';
import { vehicles, parkingAreas } from '../../mockData';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface VehicleManagementProps {}

// Get status badge color
const getStatusColor = (status: string) => {
  return status === 'PARKED' ? 'emerald' : 'gray';
};

// Get severity color
const getSeverityColor = (severity: string) => {
  switch (severity) {
    case 'MINOR':
      return 'emerald';
    case 'MODERATE':
      return 'amber';
    case 'SEVERE':
      return 'red';
    default:
      return 'gray';
  }
};

const VehicleManagement: React.FC<VehicleManagementProps> = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredStatus, setFilteredStatus] = useState<string>('all');
  const [filteredParkingType, setFilteredParkingType] = useState<string>('all');
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);

  // Filter vehicles based on search and filters
  const filteredVehicles = vehicles.filter(vehicle => {
    // Search filter
    const searchMatches = 
      vehicle.licensePlate.toLowerCase().includes(searchQuery.toLowerCase()) ||
      vehicle.make.toLowerCase().includes(searchQuery.toLowerCase()) ||
      vehicle.model.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (vehicle.customerDetails?.name.toLowerCase().includes(searchQuery.toLowerCase()) || false);
    
    // Status filter
    const statusMatches = filteredStatus === 'all' || vehicle.status === filteredStatus;
    
    // Parking type filter
    const area = parkingAreas.find(a => a.id === vehicle.areaId);
    const parkingTypeMatches = filteredParkingType === 'all' || 
      (area && area.type === filteredParkingType);
    
    return searchMatches && statusMatches && parkingTypeMatches;
  });

  return (
    <div className="space-y-6">
      <Card>
        <Text className='text-contentColor font-medium text-lg'>Vehicle Management</Text>
        
        <TabGroup>
          <TabList className="mt-4">
            <Tab>All Vehicles</Tab>
            <Tab>Parked Vehicles</Tab>
            <Tab>Vehicle History</Tab>
            <Tab>Defect Reports</Tab>
          </TabList>
          
          <div className="mt-4 flex flex-col md:flex-row gap-4">
            <TextInput 
              placeholder="Search by license plate, make, model..." 
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="md:w-1/3"
            />
            
            <div className="flex gap-2">
              <Select 
                placeholder="Status" 
                value={filteredStatus} 
                onValueChange={setFilteredStatus}
              >
                <SelectItem value="all"><Text className='text-contentColor'>All Statuses</Text></SelectItem>
                <SelectItem value="PARKED"><Text className='text-contentColor'>Parked</Text></SelectItem>
                <SelectItem value="LEFT"><Text className='text-contentColor'>Left</Text></SelectItem>
              </Select>
              
              <Select 
                placeholder="Parking Type" 
                value={filteredParkingType} 
                onValueChange={setFilteredParkingType}
              >
                <SelectItem value="all"><Text className='text-contentColor'>All Areas</Text></SelectItem>
                <SelectItem value={ParkingType.DEDICATED}><Text className='text-contentColor'>Dedicated</Text></SelectItem>
                <SelectItem value={ParkingType.RENTED}><Text className='text-contentColor'>Rented</Text></SelectItem>
                <SelectItem value={ParkingType.PUBLIC}><Text className='text-contentColor'>Public</Text></SelectItem>
              </Select>
            </div>
          </div>
          
          <TabPanels>
            {/* All Vehicles Tab */}
            <TabPanel>
              <div className="mt-4">
                <Card>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableHeaderCell><Text className='text-contentColor'>License Plate</Text></TableHeaderCell>
                        <TableHeaderCell><Text className='text-contentColor'>Vehicle</Text></TableHeaderCell>
                        <TableHeaderCell><Text className='text-contentColor'>Customer</Text></TableHeaderCell>
                        <TableHeaderCell><Text className='text-contentColor'>Check-in</Text></TableHeaderCell>
                        <TableHeaderCell><Text className='text-contentColor'>Location</Text></TableHeaderCell>
                        <TableHeaderCell><Text className='text-contentColor'>Status</Text></TableHeaderCell>
                        <TableHeaderCell><Text className='text-contentColor'>Actions</Text></TableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredVehicles.map(vehicle => {
                        const area = parkingAreas.find(a => a.id === vehicle.areaId);
                        const slot = area?.slots.find(s => s.id === vehicle.slotId);
                        
                        return (
                          <TableRow key={vehicle.id} className="cursor-pointer hover:bg-background-layer1" onClick={() => setSelectedVehicle(vehicle)}>
                            <TableCell className="font-medium">{vehicle.licensePlate}</TableCell>
                            <TableCell>{vehicle.make} {vehicle.model} ({vehicle.color})</TableCell>
                            <TableCell>
                              <div>
                                {vehicle.customerDetails?.name}
                                {vehicle.customerDetails?.isGuest && (
                                  <Badge color="blue" size="xs" className="ml-2">Hotel Guest</Badge>
                                )}
                              </div>
                              <Text className="text-xs text-gray-500">{vehicle.customerDetails?.contact}</Text>
                            </TableCell>
                            <TableCell>
                              {new Date(vehicle.checkInTime).toLocaleDateString()}
                              <Text className="text-xs text-gray-500">{new Date(vehicle.checkInTime).toLocaleTimeString()}</Text>
                            </TableCell>
                            <TableCell>
                              <div>
                                {area?.name}
                              </div>
                              <Text className="text-xs text-gray-500">Slot {slot?.name}</Text>
                            </TableCell>
                            <TableCell>
                              <Badge color={getStatusColor(vehicle.status)} size="xs">
                                {vehicle.status}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex space-x-1">
                                <button className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200">
                                  View
                                </button>
                                {vehicle.status === 'PARKED' && (
                                  <button className="px-2 py-1 text-xs bg-amber-100 text-amber-700 rounded hover:bg-amber-200">
                                    Check-out
                                  </button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Card>
              </div>
            </TabPanel>
            
            {/* Parked Vehicles Tab */}
            <TabPanel>
              <div className="mt-4">
                <Card>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableHeaderCell><Text className='text-contentColor'>License Plate</Text></TableHeaderCell>
                        <TableHeaderCell><Text className='text-contentColor'>Vehicle</Text></TableHeaderCell>
                        <TableHeaderCell><Text className='text-contentColor'>Duration</Text></TableHeaderCell>
                        <TableHeaderCell><Text className='text-contentColor'>Charges</Text></TableHeaderCell>
                        <TableHeaderCell><Text className='text-contentColor'>Location</Text></TableHeaderCell>
                        <TableHeaderCell><Text className='text-contentColor'>Actions</Text></TableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredVehicles
                        .filter(v => v.status === 'PARKED')
                        .map(vehicle => {
                          const area = parkingAreas.find(a => a.id === vehicle.areaId);
                          const slot = area?.slots.find(s => s.id === vehicle.slotId);
                          
                          // Calculate current duration
                          const checkInTime = new Date(vehicle.checkInTime);
                          const now = new Date();
                          const durationHours = Math.round((now.getTime() - checkInTime.getTime()) / (1000 * 60 * 60));
                          
                          return (
                            <TableRow key={vehicle.id} className="cursor-pointer hover:bg-background-layer1" onClick={() => setSelectedVehicle(vehicle)}>
                              <TableCell className="font-medium">{vehicle.licensePlate}</TableCell>
                              <TableCell>{vehicle.make} {vehicle.model}</TableCell>
                              <TableCell>
                                <div>
                                  {durationHours} hours
                                </div>
                                <Text className="text-xs text-gray-500">
                                  Since {checkInTime.toLocaleString()}
                                </Text>
                              </TableCell>
                              <TableCell>
                                <div className="font-medium">₹{vehicle.charges?.total || 0}</div>
                                <Badge color={vehicle.charges?.paid ? 'emerald' : 'amber'} size="xs">
                                  {vehicle.charges?.paid ? 'Paid' : 'Pending'}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <div>
                                  {area?.name}
                                </div>
                                <Text className="text-xs text-gray-500">Slot {slot?.name}</Text>
                              </TableCell>
                              <TableCell>
                                <div className="flex space-x-1">
                                  <button className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200">
                                    View
                                  </button>
                                  <button className="px-2 py-1 text-xs bg-amber-100 text-amber-700 rounded hover:bg-amber-200">
                                    Check-out
                                  </button>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </Card>
              </div>
            </TabPanel>
            
            {/* Vehicle History Tab */}
            <TabPanel>
              <div className="mt-4">
                <p className="text-center text-contentColor">
                  Vehicle history will be displayed here
                </p>
              </div>
            </TabPanel>
            
            {/* Defect Reports Tab */}
            <TabPanel>
              <div className="mt-4">
                <Card>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableHeaderCell><Text className='text-contentColor'>Vehicle</Text></TableHeaderCell>
                        <TableHeaderCell><Text className='text-contentColor'>Defect</Text></TableHeaderCell>
                        <TableHeaderCell><Text className='text-contentColor'>Location</Text></TableHeaderCell>
                        <TableHeaderCell><Text className='text-contentColor'>Severity</Text></TableHeaderCell>
                        <TableHeaderCell><Text className='text-contentColor'>Report Date</Text></TableHeaderCell>
                        <TableHeaderCell><Text className='text-contentColor'>Actions</Text></TableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredVehicles
                        .filter(v => v.defects && v.defects.length > 0)
                        .flatMap(vehicle => 
                          vehicle.defects.map(defect => ({
                            vehicleId: vehicle.id,
                            licensePlate: vehicle.licensePlate,
                            make: vehicle.make,
                            model: vehicle.model,
                            checkInTime: vehicle.checkInTime,
                            defect
                          }))
                        )
                        .map((item, index) => (
                          <TableRow key={`${item.vehicleId}-${item.defect.id}`} className="cursor-pointer hover:bg-background-layer1">
                            <TableCell>
                              <div className="font-medium">{item.licensePlate}</div>
                              <Text className="text-xs text-gray-500">{item.make} {item.model}</Text>
                            </TableCell>
                            <TableCell>{item.defect.description}</TableCell>
                            <TableCell>{item.defect.location}</TableCell>
                            <TableCell>
                              <Badge color={getSeverityColor(item.defect.severity)} size="xs">
                                {item.defect.severity}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {new Date(item.checkInTime).toLocaleDateString()}
                            </TableCell>
                            <TableCell>
                              <button className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200">
                                View Details
                              </button>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </Card>
              </div>
            </TabPanel>
          </TabPanels>
        </TabGroup>
      </Card>
      
      {/* Vehicle Details Modal */}
      {selectedVehicle && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-background rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start">
              <Text className='text-contentColor font-medium text-lg'>{selectedVehicle.make} {selectedVehicle.model}</Text>
              <button 
                className="text-contentColor hover:text-contentColor-layer1" 
                onClick={() => setSelectedVehicle(null)}
              >
                <XMarkIcon className='w-6 h-6' />
              </button>
            </div>
            
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <Text className="text-sm text-gray-500">License Plate</Text>
                <p className="font-semibold text-primary">{selectedVehicle.licensePlate}</p>
              </div>
              <div>
                <Text className="text-sm text-gray-500">Vehicle Type</Text>
                <p className='text-primary'>{selectedVehicle.type}</p>
              </div>
              <div>
                <Text className="text-sm text-gray-500">Color</Text>
                <p className='text-primary'>{selectedVehicle.color}</p>
              </div>
              <div>
                <Text className="text-sm text-gray-500">Year</Text>
                <p className='text-primary'  >{selectedVehicle.year}</p>
              </div>
            </div>
            
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold text-contentColor">Parking Details</h3>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <Text className="text-sm text-gray-500">Area</Text>
                  <p className='text-primary'>{parkingAreas.find(a => a.id === selectedVehicle.areaId)?.name || '-'}</p>
                  
                  <Text className="text-sm text-gray-500">Slot</Text>
                  <p className='text-primary'>{
                    parkingAreas
                      .find(a => a.id === selectedVehicle.areaId)
                      ?.slots.find(s => s.id === selectedVehicle.slotId)
                      ?.name || '-'
                  }</p>
                  
                  <Text className="text-sm text-gray-500">Check-in Time</Text>
                  <p className='text-primary'>{new Date(selectedVehicle.checkInTime).toLocaleString()}</p>
                  
                  <Text className="text-sm text-gray-500">Status</Text>
                  <Badge color={getStatusColor(selectedVehicle.status)}>
                    {selectedVehicle.status}
                  </Badge>
                  
                  <Text className="text-sm text-gray-500">Expected Duration</Text>
                  <p className='text-primary'>{selectedVehicle.expectedDuration} hours</p>
                  
                  <Text className="text-sm text-gray-500">Handled By</Text>
                  <p className='text-primary'>{selectedVehicle.handledBy}</p>
                </div>
              </div>
              
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold text-contentColor">Customer Details</h3>
                {selectedVehicle.customerDetails && (
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <Text className="text-sm text-gray-500">Name</Text>
                    <p className='text-primary'>{selectedVehicle.customerDetails.name}</p>
                    
                    <Text className="text-sm text-gray-500">Contact</Text>
                    <p className='text-primary'>{selectedVehicle.customerDetails.contact}</p>
                    
                    {selectedVehicle.customerDetails.email && (
                      <>
                        <Text className="text-sm text-gray-500">Email</Text>
                        <p className='text-primary'>{selectedVehicle.customerDetails.email}</p>
                      </>
                    )}
                    
                    <Text className="text-sm text-gray-500">Guest Status</Text>
                    <div>
                      {selectedVehicle.customerDetails.isGuest ? (
                        <div>
                          <Badge color="blue" size="xs">Hotel Guest</Badge>
                          {selectedVehicle.customerDetails.roomNumber && (
                            <p className="text-sm ml-2 text-primary">Room {selectedVehicle.customerDetails.roomNumber}</p>
                          )}
                        </div>
                      ) : (
                        <Badge color="gray" size="xs">Non-Guest</Badge>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="mt-4 border rounded-lg p-4">
              <h3 className="font-semibold text-contentColor">Charges</h3>
              {selectedVehicle.charges && (
                <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <Text className="text-sm text-gray-500">Base Rate</Text>
                    <p className='text-primary'>₹{selectedVehicle.charges.base}</p>
                  </div>
                  <div>
                    <Text className="text-sm text-gray-500">Additional</Text>
                    <p className='text-primary'>₹{selectedVehicle.charges.additional}</p>
                  </div>
                  <div>
                    <Text className="text-sm text-gray-500">Total</Text>
                    <p className="font-semibold text-primary">₹{selectedVehicle.charges.total}</p>
                  </div>
                  <div>
                    <Text className="text-sm text-gray-500">Payment Status</Text>
                    <Badge color={selectedVehicle.charges.paid ? 'emerald' : 'amber'}>
                      {selectedVehicle.charges.paid ? 'Paid' : 'Pending'}
                    </Badge>
                  </div>
                </div>
              )}
            </div>
            
            {/* Vehicle Defects */}
            {selectedVehicle.defects && selectedVehicle.defects.length > 0 && (
              <div className="mt-4 border rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <h3 className="font-semibold text-contentColor">Vehicle Defects</h3>
                  <Badge color="red" size="xs">
                    {selectedVehicle.defects.length} {selectedVehicle.defects.length === 1 ? 'Issue' : 'Issues'}
                  </Badge>
                </div>
                
                <div className="mt-3 space-y-3">
                  {selectedVehicle.defects.map(defect => (
                    <div key={defect.id} className="border-l-4 pl-3 py-2" style={{
                      borderColor: defect.severity === 'SEVERE' 
                        ? '#EF4444' 
                        : defect.severity === 'MODERATE' 
                          ? '#F59E0B' 
                          : '#10B981'
                    }}>
                      <div className="flex justify-between">
                        <p className="font-medium text-primary">{defect.description}</p>
                        <Badge color={getSeverityColor(defect.severity)}>
                          {defect.severity}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">Location: {defect.location}</p>
                      {defect.notes && <p className="text-sm italic text-gray-500 mt-1">{defect.notes}</p>}
                      <div className="mt-1 text-xs text-gray-500">
                        {defect.reportedAtCheckIn ? 'Reported at check-in' : 'Discovered after check-in'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="mt-6 flex justify-end space-x-2">
              {selectedVehicle.status === 'PARKED' ? (
                <>
                  <button className="px-4 py-2 bg-amber-600 text-white rounded hover:bg-amber-700">
                    Checkout Vehicle
                  </button>
                  <button className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300">
                    Edit Details
                  </button>
                </>
              ) : (
                <button className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300">
                  View History
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleManagement;